import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  serializeNumber,
  validation,
  validationError,
} from './shared.ts';

interface NavigatorWithConnection extends Navigator {
  readonly connection?: {
    readonly downlink?: number;
    readonly downlinkMax?: number;
    readonly effectiveType?: string;
    readonly rtt?: number;
    readonly saveData?: boolean;
    readonly type?: string;
  };
}

async function collect(): Promise<RawData> {
  const connection = (navigator as NavigatorWithConnection).connection;
  return {
    type: connection?.type ?? null,
    downlinkMax: serializeNumber(connection?.downlinkMax),
    effectiveType: connection?.effectiveType ?? null,
    rtt: serializeNumber(connection?.rtt),
    downlink: serializeNumber(connection?.downlink),
    saveData: connection?.saveData ?? null,
  };
}

function isNonnegativeNumber(value: unknown, allowInfinity: boolean): boolean {
  if (allowInfinity && value == 'Infinity') {
    return true;
  }
  return typeof value == 'number' && Number.isFinite(value) && value >= 0;
}

function validateNetwork(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (
    rawData.type !== null &&
    ![
      'cellular',
      'bluetooth',
      'ethernet',
      'wifi',
      'wimax',
      'other',
      'none',
      'unknown',
    ].includes(String(rawData.type))
  ) {
    reasons.push('type is not a normal Network Information value');
  }
  if (
    rawData.downlinkMax !== null &&
    !isNonnegativeNumber(rawData.downlinkMax, true)
  ) {
    reasons.push('downlinkMax must be a nonnegative number or Infinity');
  }
  if (
    rawData.effectiveType !== null &&
    !['slow-2g', '2g', '3g', '4g'].includes(String(rawData.effectiveType))
  ) {
    reasons.push('effectiveType is not a normal Network Information value');
  }
  if (
    rawData.rtt !== null &&
    (!isNonnegativeNumber(rawData.rtt, false) || !Number.isInteger(rawData.rtt))
  ) {
    reasons.push('rtt must be a nonnegative integer');
  }
  if (
    rawData.downlink !== null &&
    !isNonnegativeNumber(rawData.downlink, true)
  ) {
    reasons.push('downlink must be a nonnegative number or Infinity');
  }
  if (rawData.saveData !== null && typeof rawData.saveData != 'boolean') {
    reasons.push('saveData is not boolean or null');
  }
  return validation(reasons);
}

function compactNumber(value: unknown): string {
  if (value == 'Infinity') {
    return '∞';
  }
  if (typeof value != 'number') {
    return '-';
  }
  const text = Number(value.toFixed(2)).toString();
  return text.length <= 5 ? text : value.toExponential(0).replace('e+', 'e');
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  if (
    rawData.type === null &&
    rawData.downlinkMax === null &&
    rawData.effectiveType === null &&
    rawData.rtt === null &&
    rawData.downlink === null &&
    rawData.saveData === null
  ) {
    return 'Unavailable';
  }
  const typeCodes: Record<string, string> = {
    cellular: 'c',
    bluetooth: 'b',
    ethernet: 'e',
    wifi: 'w',
    wimax: 'x',
    other: 'o',
    none: 'n',
    unknown: 'u',
  };
  const effectiveTypeCodes: Record<string, string> = {
    'slow-2g': 's2',
    '2g': '2g',
    '3g': '3g',
    '4g': '4g',
  };
  const type = typeCodes[String(rawData.type)] ?? '-';
  const effectiveType =
    effectiveTypeCodes[String(rawData.effectiveType)] ?? '-';
  const saveData =
    typeof rawData.saveData == 'boolean' ? Number(rawData.saveData) : '-';
  return `${type}/${effectiveType} d${compactNumber(rawData.downlink)} m${compactNumber(rawData.downlinkMax)} r${compactNumber(rawData.rtt)} s${saveData}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateNetwork(rawData);
}

export const networkInformationTest: TestDefinition = {
  id: 'networkInformation',
  title: 'Network information',
  category: 'variableProfile',
  format,
  run,
  validate,
};
